import { useMemo } from "react";
import {
  Badge,
  Card,
  Divider,
  Group,
  Stack,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import {
  IconBolt,
  IconCheck,
  IconCircleCheck,
  IconCircleX,
  IconHandStop,
  IconPlayerPlay,
  IconTournament,
  IconX,
} from "@tabler/icons-react";
import {
  LuaBlock,
  PresetCallout,
  useStepRuleSet,
} from "./components/SharedRuleParts";
import {
  ACTION_COLORS,
  ACTION_LABELS,
  AdvancementRuleView,
  TRIGGER_LABELS,
} from "./types";
import { useRuleFireCounts } from "./validation/validation.service";

const ADVANCEMENT_SKELETON_HEIGHTS = [28, 56, 180, 180] as const;

interface RuleCardProps {
  rule: AdvancementRuleView;
  fireCount: number | null;
}

const RuleCard = ({ rule, fireCount }: RuleCardProps) => (
  <Card withBorder>
    <Stack gap="sm">
      <Group justify="space-between" wrap="nowrap">
        <Group gap="xs" wrap="nowrap">
          <Badge variant="filled" color="gray" size="sm">
            #{rule.order + 1}
          </Badge>
          <Text fw={600}>{rule.name}</Text>
          {rule.purpose === "MATCH_SERIES_RESOLUTION" && (
            <Badge variant="light" color="violet" size="xs">
              Match series resolution
            </Badge>
          )}
        </Group>
        <Group gap="xs">
          {fireCount !== null && (
            <Tooltip
              label={
                fireCount >= 1
                  ? `Fired ${fireCount} time${fireCount === 1 ? "" : "s"} in last validation`
                  : "Never fired in last validation"
              }
            >
              <Badge
                size="sm"
                color={fireCount >= 1 ? "green" : "red"}
                variant="light"
                leftSection={
                  fireCount >= 1 ? (
                    <IconCircleCheck size={12} />
                  ) : (
                    <IconCircleX size={12} />
                  )
                }
              >
                {fireCount >= 1 ? `${fireCount}×` : "0×"}
              </Badge>
            </Tooltip>
          )}
          {rule.enabled ? (
            <Badge
              size="sm"
              color="green"
              variant="light"
              leftSection={<IconCheck size={12} />}
            >
              Enabled
            </Badge>
          ) : (
            <Badge
              size="sm"
              color="gray"
              variant="light"
              leftSection={<IconX size={12} />}
            >
              Disabled
            </Badge>
          )}
        </Group>
      </Group>

      {rule.description && (
        <Text size="sm" c="dimmed">
          {rule.description}
        </Text>
      )}

      <Group gap="xs">
        <Badge
          variant="light"
          color="cyan"
          leftSection={<IconBolt size={12} />}
        >
          Trigger: {TRIGGER_LABELS[rule.triggerType]}
        </Badge>
        {rule.autoExecute && (
          <Badge
            variant="light"
            color="teal"
            leftSection={<IconPlayerPlay size={12} />}
          >
            Auto-execute
          </Badge>
        )}
        {rule.allowManualOverride && (
          <Badge
            variant="light"
            color="orange"
            leftSection={<IconHandStop size={12} />}
          >
            Manual override allowed
          </Badge>
        )}
      </Group>

      {rule.triggerScope && (
        <LuaBlock
          label="Trigger scope"
          value={rule.triggerScope}
          maxHeight={200}
        />
      )}

      <LuaBlock
        label="Condition"
        value={rule.condition || null}
        maxHeight={260}
      />

      <Divider label="Actions" labelPosition="left" />

      {rule.actions.length === 0 ? (
        <Text size="sm" c="dimmed" fs="italic">
          No actions configured
        </Text>
      ) : (
        <Stack gap="sm">
          {rule.actions.map((action, idx) => (
            <Card
              key={`${rule.id}-action-${idx}-${action.type}`}
              withBorder
              padding="sm"
              radius="sm"
            >
              <Stack gap="xs">
                <Group gap="xs">
                  <Badge color={ACTION_COLORS[action.type]} variant="light">
                    {ACTION_LABELS[action.type]}
                  </Badge>
                </Group>
                {action.script && (
                  <LuaBlock
                    label="Script"
                    value={action.script}
                    maxHeight={220}
                  />
                )}
              </Stack>
            </Card>
          ))}
        </Stack>
      )}
    </Stack>
  </Card>
);

export const AdvancementEditor = () => {
  const { ruleSet, fallback } = useStepRuleSet({
    title: "Advancement Rules",
    icon: <IconTournament size={20} />,
    skeletonHeights: ADVANCEMENT_SKELETON_HEIGHTS,
  });

  const fireCountMap = useRuleFireCounts(ruleSet?.id ?? null);

  const rules = useMemo(
    () =>
      [...(ruleSet?.advancementRules ?? [])].sort((a, b) => a.order - b.order),
    [ruleSet?.advancementRules],
  );

  if (!ruleSet) return fallback;

  return (
    <Stack>
      <Group gap="xs">
        <IconTournament size={20} />
        <Title order={2}>Advancement Rules</Title>
        <Badge variant="light" size="lg">
          {rules.length}
        </Badge>
      </Group>

      <PresetCallout />

      {rules.length === 0 ? (
        <Text size="sm" c="dimmed" fs="italic">
          No advancement rules configured
        </Text>
      ) : (
        <Stack gap="md">
          {rules.map((rule) => (
            <RuleCard
              key={rule.id}
              rule={rule}
              fireCount={
                fireCountMap !== null ? (fireCountMap.get(rule.id) ?? 0) : null
              }
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
};
